import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTranslate } from "react-polyglot";
import clsx from "clsx";
import { Box, makeStyles, TextField, Typography, Divider, Button, Checkbox, Chip } from "@material-ui/core";
import ToggleButton from "../../../../SharedComponents/ToggleButton.jsx";
import LearnAudioPlayer from "./SharedComponents/LearnAudioPlayer.jsx";
import FlexboxVertical from "../../../../SharedComponents/FlexboxVertical.jsx";
import FlexboxHorizontal from "../../../../SharedComponents/FlexboxHorizontal.jsx";
import { DEFAULT_LEARNED_PATCH } from "../../../../Providers/Data/LearnProcessProvider.jsx";
import Question from "./SharedComponents/Question.jsx";
import Appendix from "./SharedComponents/Appendix.jsx";
import WrongAnswerAlertComment from "./SharedComponents/WrongAnswerAlertComment.jsx";
import formatTextSegment from "./SharedComponents/formatTextSegment.js";

/**
 * Нормализует строку: удаляет начальные и конечные пробелы, схлопывает последовательности пробельных символов в один пробел
 * @param {string} value - строка для нормализации
 * @returns {string} нормализованная строка в нижнем регистре
 */
const normalizeSpaces = (value) => value.trim().replace(/\s+/g, " ").toLowerCase();

const useStyles = makeStyles((theme) => ({
  answersGroup: {
    margin: "20px 0",
  },
  exerciseTitle: {
    fontSize: 24,
    fontWeight: 500,
    textAlign: "center",
    marginTop: 20,
  },
  textAnswer: {
    flex: "0 0 40%",
    margin: "0 5% 20px",
    display: "inline",
  },
  imageAnswerButton: {
    flex: "0 0 20%",
    maxWidth: 184,
    minWidth: 184,
    maxHeight: 184,
    padding: 7,
    borderRadius: 7,
    margin: "0 1.7% 20px",
    "&.selected": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  imageAnswer: {
    maxWidth: 170,
    height: 170,
    objectFit: "contain",
    cursor: "pointer",
  },
  keyboardAnswer: {
    margin: "60px 0",
    width: "80%",
  },
  rightAnswerChip: {
    fontSize: "0.875rem",
    fontWeight: 500,
    margin: 4,
  },
}));

/**
 * @param {Object} classes
 * @param {ExerciseType11} exercise
 * @param {?boolean[]} answerValue
 * @param {String} keybAnswerValue
 * @param {Function} answerHandler
 * @param {FnStringVoid} keyboardAnswerHandler
 * @param {Boolean} disabledChoice
 * @returns {*}
 * @constructor
 */
const Answers = ({
  classes,
  exercise,
  answerValue,
  keybAnswerValue,
  answerHandler,
  keyboardAnswerHandler,
  disabledChoice,
}) => {
  const t = useTranslate();
  const answer = exercise.answer;

  /**
   * @param {Boolean} isRight
   * @param {Number} index
   */
  const composeAnswerHandler = (isRight, index) => {
    return () => {
      answerHandler({ target: { value: { isRight, index } } });
    };
  };

  return (
    <>
      {answer.type === "select_audio" && (
        <FlexboxVertical className={classes.answersGroup}>
          {answer.selectAudio.map(
            /** @param {SingleMiniAnswer<RemoteMedia>} item
             * @param {Number} index */
            (item, index) => (
              <FlexboxHorizontal
                data-tag="answer item"
                key={item.answer.path}
                justifyContent="center"
                alignItems="center"
                marginTop="20px"
              >
                <Checkbox
                  checked={answerValue[index] !== null}
                  color="primary"
                  onChange={composeAnswerHandler(item.right, index)}
                  disabled={disabledChoice}
                />
                <LearnAudioPlayer url={item.answer.url} />
              </FlexboxHorizontal>
            ),
          )}
        </FlexboxVertical>
      )}
      {answer.type === "select_text" && (
        <Box
          data-tag="text answers"
          width="100%"
          display="flex"
          margin="30px 0 0"
          flexWrap="wrap"
          justifyContent="flex-start"
        >
          {answer.selectText.map(
            /** @param {SingleMiniAnswer<String>} item
             * @param {Number} index */
            (item, index) => (
              <ToggleButton
                className={classes.textAnswer}
                key={uuidv4()}
                onChange={composeAnswerHandler(item.right, index)}
                disabled={disabledChoice}
                value={answerValue[index] !== null}
              >
                {formatTextSegment(item.answer).nodes}
              </ToggleButton>
            ),
          )}
        </Box>
      )}
      {answer.type === "select_image" && (
        <Box
          data-tag="image answers"
          width="100%"
          display="flex"
          margin="30px 0 0"
          flexWrap="wrap"
          justifyContent="flex-start"
        >
          {answer.selectImage.map((item, index) => (
            <Button
              key={item.answer.path}
              className={clsx(classes.imageAnswerButton, { selected: answerValue[index] !== null })}
              onClick={composeAnswerHandler(item.right, index)}
              disabled={disabledChoice}
            >
              <img className={classes.imageAnswer} src={item.answer.url} alt={item.answer.name} />
            </Button>
          ))}
        </Box>
      )}
      {answer.type === "keyboard" && (
        <TextField
          className={classes.keyboardAnswer}
          placeholder={t("Exercises.typeAnswer")}
          variant="outlined"
          value={keybAnswerValue}
          onChange={keyboardAnswerHandler}
          disabled={disabledChoice}
        />
      )}
    </>
  );
};

Answers.propTypes = {
  answerHandler: PropTypes.func.isRequired,
  answerValue: PropTypes.any.isRequired,
  classes: PropTypes.object.isRequired,
  exercise: PropTypes.object.isRequired,
  keybAnswerValue: PropTypes.string,
  keyboardAnswerHandler: PropTypes.func,
  disabledChoice: PropTypes.bool,
};

/**
 * Удаляет маркеры форматирования (двойная звёздочка и двойной слеш) из строки.
 * Применяется перед отправкой ответа ученика в API, где разметка не нужна.
 *
 * @param {String} text - Исходная строка с возможными маркерами форматирования.
 * @returns {String} Очищенная строка без маркеров `**` и `//`.
 */
const stripFormatMarkers = (text) => (text ? text.replace(/\*\*|\/\//g, "") : text);

/**
 * Возвращает выбранные варианты отдельно, сохраняя границы текста с запятыми.
 *
 * @param {Object} answer
 * @param {Array} answerValue
 * @param {string} keybAnswerValue
 * @returns {string[]}
 */
function composeAnswerValuesForApi(answer, answerValue, keybAnswerValue) {
  let answerValueForApi;
  if (answer.type !== "keyboard") {
    const selectedOptions = [];
    switch (answer.type) {
      case "select_text":
        answer.selectText.forEach((it, i) => {
          if (answerValue[i] !== null) selectedOptions.push(stripFormatMarkers(it.answer));
        });
        break;
      case "select_image":
        answer.selectImage.forEach((it, i) => {
          if (answerValue[i] !== null) selectedOptions.push(it.answer.url);
        });
        break;
      case "select_audio":
        answer.selectAudio.forEach((it, i) => {
          if (answerValue[i] !== null) selectedOptions.push(it.answer.url);
        });
        break;
    }
    answerValueForApi = selectedOptions;
  } else {
    answerValueForApi = [keybAnswerValue];
  }
  return answerValueForApi;
}

//
//
//
//
//
//
//
//
//
//

/**
 * @param {ExerciseType11} exercise
 * @param {Function} setCheckButtonDisabled
 * @param {String} exerciseTitle
 * @param {MutableRefObject<Function>} checkAnswerRef
 * @param {FnComponentStringVoid} showRightAnswer
 * @param {FnComponentArrayStringVoid} showRightAnswerWithComments
 * @param {Function} setVirtualClick
 * @param {Boolean} isExam
 * @param {Boolean} disabledChoice
 * @param {boolean} isHiddenCheckButton - Признак скрытия кнопки проверки (не используется в Exercise11, но передаётся родителем; деструктурируется, чтобы не попасть на DOM-элемент через rest).
 * @param {Object} rest
 * @returns {*}
 * @constructor
 */
const Exercise11 = ({
  exercise,
  setCheckButtonDisabled,
  exerciseTitle,
  checkAnswerRef,
  showRightAnswer,
  showRightAnswerWithComments,
  setVirtualClick,
  isExam,
  disabledChoice,
  isHiddenCheckButton,
  ...rest
}) => {
  const classes = useStyles();
  const t = useTranslate();

  const [answerValue, setAnswerValue] = useState(/** @type {?boolean[]} */ []);
  const [keybAnswerValue, setKeybAnswerValue] = useState("");
  const [resetAnswerTrigger, setResetAnswerTrigger] = useState(false);
  const [isSingleAnswer, setIsSingleAnswer] = useState(false);
  const answer = exercise.answer;

  //reset state
  useEffect(() => {
    // определяем сколько правильных ответов (один или несколько)
    let answerArrayRight;
    switch (answer.type) {
      case "select_image":
        answerArrayRight = answer.selectImage.filter((item) => item.right);
        break;
      case "select_text":
        answerArrayRight = answer.selectText.filter((item) => item.right);
        break;
      case "select_audio":
        answerArrayRight = answer.selectAudio.filter((item) => item.right);
        break;
      default:
        answerArrayRight = [];
        break;
    }

    setIsSingleAnswer(answerArrayRight.length === 1);

    setAnswerValue(
      (answer.selectText && answer.selectText.length > 0 && answer.selectText.map(() => null)) ||
        (answer.selectImage && answer.selectImage.length > 0 && answer.selectImage.map(() => null)) ||
        (answer.selectAudio && answer.selectAudio.length > 0 && answer.selectAudio.map(() => null)) ||
        [],
    );
    setKeybAnswerValue("");
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercise, resetAnswerTrigger]);

  // Если  стоит настройка отвечать в один клик, то при наличии хоть какого-то ответа флаг виртуального клика меняется на true
  useEffect(() => {
    if (answer?.oneClickAnswer && answerValue.find((item) => item !== null) !== undefined) {
      const timer = setTimeout(() => {
        setVirtualClick && setVirtualClick(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer, answerValue]);

  checkAnswerRef.current = () => {
    let learned = true;
    let comments = [];
    /** @param {SingleMiniAnswer} Answer
     * @param {Number} index */
    const answerChecker = (Answer, index) => {
      const answerValue_ = answerValue[index];
      if (answerValue_ !== null && Answer.comment) {
        comments.push(Answer.comment);
      }
      if ((Answer.right && answerValue_ === null) || (!Answer.right && answerValue_ !== null)) {
        learned = false;
      }
    };

    switch (answer.type) {
      case "select_image":
        answer.selectImage.forEach(answerChecker);
        break;
      case "select_text":
        answer.selectText.forEach(answerChecker);
        break;
      case "select_audio":
        answer.selectAudio.forEach(answerChecker);
        break;
      default:
        // Нормализуем пробелы между словами: схлопываем последовательности пробельных символов в один пробел
        learned = normalizeSpaces(answer.keyboard) === normalizeSpaces(keybAnswerValue);
        // Добавляем комментарий к ответу при типе keyboard
        if (answer.keyboardComment) {
          comments.push(answer.keyboardComment);
        }
        break;
    }

    // Передаём выбранные варианты отдельным массивом, чтобы запятые в тексте не искажали перерасчёт.
    const answerValues = composeAnswerValuesForApi(answer, answerValue, keybAnswerValue);
    const answerValueForApi = answer.type === "keyboard" ? keybAnswerValue : answerValues.join(", ");
    const answerData = { values: answerValues };

    if (!answer.requireAllAnswers && answerValue.find((item) => item === true)) {
      learned = true;
    }
    if (!learned) {
      let correctAnswerView = null;
      switch (answer.type) {
        case "select_image":
          correctAnswerView = exercise.answer.selectImage
            .filter((image) => image.right)
            .map((image, index) => (
              <img
                key={image.answer.path}
                style={{ width: "80%", marginTop: index > 0 ? 10 : undefined }}
                src={image.answer.url}
                alt=""
              />
            ));
          break;
        case "select_audio":
          correctAnswerView = exercise.answer.selectAudio
            .filter((audio) => audio.right)
            .map((audio) => (
              <FlexboxHorizontal
                data-tag="answer item"
                key={audio.answer.path}
                justifyContent="center"
                alignItems="center"
                marginTop="20px"
              >
                <LearnAudioPlayer url={audio.answer.url} />
              </FlexboxHorizontal>
            ));
          break;
        case "select_text":
          correctAnswerView = (
            <>
              {exercise.answer.selectText
                .filter((text) => text.right)
                .map((text, index) => (
                  <Chip
                    key={text.answer + index}
                    className={classes.rightAnswerChip}
                    variant="outlined"
                    label={formatTextSegment(text.answer).nodes}
                  />
                ))}
              {comments.length > 0 && (
                <>
                  <Box width="100%" padding="15px 0">
                    <Divider variant="fullWidth" />
                  </Box>
                  <WrongAnswerAlertComment comments={comments} />
                </>
              )}
            </>
          );
          break;
        case "keyboard":
          correctAnswerView = (
            <>
              <Typography>{exercise.answer.keyboard}</Typography>
              {comments.length > 0 && (
                <>
                  <Box width="100%" padding="15px 0">
                    <Divider variant="fullWidth" />
                  </Box>
                  <WrongAnswerAlertComment comments={comments} />
                </>
              )}
            </>
          );
          break;
      }
      if (answer.type === "select_text" || answer.type === "keyboard") {
        showRightAnswerWithComments(correctAnswerView, comments, answerValueForApi, answerData);
      } else {
        showRightAnswer(correctAnswerView, answerValueForApi, answerData);
      }
      setResetAnswerTrigger(!resetAnswerTrigger);
      if (isExam) {
        return { ...DEFAULT_LEARNED_PATCH, learned, payload: { answerValue: answerValueForApi, answerData } };
      }
      return null;
    } else {
      showRightAnswerWithComments(null, comments, answerValueForApi, answerData);
      return { ...DEFAULT_LEARNED_PATCH, payload: { answerValue: answerValueForApi, answerData } };
    }
  };

  /** @param {IReactEventTarget<{isRight: Boolean, index: Number}>} target */
  const answerHandler = ({ target }) => {
    const { isRight, index } = target.value;
    let newArray;
    if (isSingleAnswer) {
      newArray = answerValue.map((item, i) => (i === index ? item : null));
    } else {
      newArray = [...answerValue];
    }
    if (newArray[index] === null) {
      newArray[index] = isRight;
    } else {
      newArray[index] = null;
    }
    setAnswerValue(newArray);
    setCheckButtonDisabled(newArray.findIndex((element) => element !== null) === -1);
  };

  /** @param {String} value */
  const keyboardAnswerHandler = ({ target: { value } }) => {
    setKeybAnswerValue(value);
    setCheckButtonDisabled(!value);
  };

  return (
    <Box {...rest} data-component="Exercise11" display="flex" flexDirection="column" alignItems="center">
      <Question
        exerciseQuestion={exercise.question}
        exerciseTitle={exerciseTitle}
        defaultTitle={
          exercise.answer.type === "keyboard"
            ? t("Exercises.type11.title.keyboard")
            : t("Exercises.type11.title.select")
        }
      />
      {exercise.appendix && exercise.appendix.pdf && <Appendix pdfUrl={exercise.appendix.pdf.url} />}
      <Answers
        classes={classes}
        exercise={exercise}
        answerValue={answerValue}
        keybAnswerValue={keybAnswerValue}
        answerHandler={answerHandler}
        keyboardAnswerHandler={keyboardAnswerHandler}
        disabledChoice={disabledChoice}
      />
    </Box>
  );
};

Exercise11.propTypes = {
  checkAnswerRef: PropTypes.object.isRequired,
  exercise: PropTypes.object.isRequired,
  exerciseTitle: PropTypes.string,
  setCheckButtonDisabled: PropTypes.func.isRequired,
  isHiddenCheckButton: PropTypes.bool,
  showRightAnswer: PropTypes.func.isRequired,
  showRightAnswerWithComments: PropTypes.func.isRequired,
  setVirtualClick: PropTypes.func,
  isExam: PropTypes.bool,
  disabledChoice: PropTypes.bool,
};

export default Exercise11;
